import { useMemo, useCallback } from 'react'
import {
  PartialBlock,
  DefaultBlockSchema,
  BlockNoteEditor,
} from '@packages/blocknote-core'
import { Note } from '../../../utils/note'
import { TaskItem } from '../../../modules/filter/types'
import { BlockRenderer } from './BlockRenderer'
import { NoteHeaderRenderer } from './NoteHeaderRenderer'
import { type RenderableItem, type ItemVariant } from './types'
import { reconstructTaskLine } from './taskUtils'
import { shouldShowTaskSourceNote } from './taskUtils'
import { useSidebarProvider } from '../../../contexts/Sidebar'

type UniversalItemRendererProps = {
  item: RenderableItem
  variant?: ItemVariant
  showNoteHeader?: boolean
  showTaskToggle?: boolean
  className?: string
  contextNote?: Note
}

export function UniversalItemRenderer({
  item,
  variant = 'filter',
  showNoteHeader = false,
  showTaskToggle = true,
  className,
  contextNote,
}: UniversalItemRendererProps) {
  const { handleRevealNote, handleSelectRecordName } = useSidebarProvider()

  // Convert different item types to blocks
  const block = useMemo(() => {
    if (item.type === 'block') {
      return item.content as PartialBlock<DefaultBlockSchema>
    }

    if (item.type === 'task') {
      const taskItem = item.content as TaskItem
      // Always use reconstructTaskLine to ensure proper state markers are included
      // expectedContent might not have the correct [>], [x], [-] markers for parsing
      const taskLine = reconstructTaskLine(taskItem)
      return BlockNoteEditor.notePlanToBlocks(taskLine, '')[0]
    }

    if (item.type === 'note') {
      const note = item.content as Note
      // For notes, we might want to render the title or first content block
      return BlockNoteEditor.notePlanToBlocks(
        note.title ?? note.filename,
        ''
      )[0]
    }

    return
  }, [item])

  // Determine if we should show source note or scheduled date for tasks
  // TODO: For individual tasks (when grouped by date), always show source note?
  const shouldShowSource = useMemo(() => {
    if (item.type === 'task') {
      const taskItem = item.content as TaskItem
      return shouldShowTaskSourceNote(taskItem, contextNote)
    }
    return false
  }, [item, contextNote])

  // For tasks, check if we have a scheduled date to show
  const hasScheduledDate = useMemo(() => {
    if (item.type === 'task') {
      const taskItem = item.content as TaskItem
      return Boolean(taskItem.scheduledDate)
    }
    return false
  }, [item])

  // Update metadata with scheduled date for tasks
  const enhancedMetadata = useMemo(() => {
    if (item.type === 'task') {
      const taskItem = item.content as TaskItem
      return {
        ...item.metadata,
        sourceNote: taskItem.sourceNote,
        scheduledDate: taskItem.scheduledDate,
      }
    }
    return item.metadata
  }, [item])

  // Create navigation callback based on variant and recordName
  const onNavigate = useCallback(() => {
    if (!item.metadata.recordName) {
      console.warn('Navigation attempted without recordName')
      return
    }

    if (variant === 'reference') {
      handleSelectRecordName(item.metadata.recordName)
    } else {
      handleRevealNote(item.metadata.recordName)
    }
  }, [
    variant,
    item.metadata.recordName,
    handleSelectRecordName,
    handleRevealNote,
  ])

  // eslint-disable-next-line unicorn/no-null -- react uses null
  if (!block) return null

  return (
    <div className={`universal-item-renderer ${className ?? ''}`}>
      {showNoteHeader && item.metadata.sourceNote && (
        <NoteHeaderRenderer
          note={item.metadata.sourceNote as Note}
          variant={variant}
        />
      )}

      <BlockRenderer
        block={block}
        metadata={enhancedMetadata}
        onNavigate={onNavigate}
        showTaskToggle={showTaskToggle}
        showSourceNote={shouldShowSource}
        showScheduledDate={hasScheduledDate && !shouldShowSource}
      />
    </div>
  )
}
